import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { categories } from '../data/timeline';

const TimelineItemContainer = styled(motion.div)`
  display: flex;
  margin-bottom: 2rem;
  position: relative;
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    .content {
      text-align: right;
      margin-right: 2rem;
      margin-left: 0;
    }
    
    .line-connector {
      right: auto;
      left: -1px;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column !important;
    margin-bottom: 2rem;
    
    .content {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      margin-top: 1rem;
    }
    
    .line-connector {
      display: none;
    }
  }
`;

const IconContainer = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  position: relative;
  z-index: 2;
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    margin: 0 auto;
  }
`;

const TimelineYear = styled(motion.div)`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--secondary-bg);
  color: ${props => props.color};
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 2px solid ${props => props.color};
  white-space: nowrap;
  box-shadow: var(--shadow);
  z-index: 3;
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin: 0 auto 1rem;
    width: fit-content;
  }
`;

const Content = styled(motion.div)`
  flex: 1;
  max-width: 400px;
  margin-left: 2rem;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-left: 0;
  }
`;

const Card = styled(motion.div)`
  background: var(--secondary-bg);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2rem;
    left: -10px;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid var(--secondary-bg);
  }
  
  .timeline-item:nth-child(even) &::before {
    left: auto;
    right: -10px;
    border-right: none;
    border-left: 10px solid var(--secondary-bg);
  }
  
  @media (max-width: 768px) {
    &::before {
      display: none;
    }
  }
`;

const Year = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.color};
  background: ${props => props.bgColor};
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 0.75rem;
  letter-spacing: 0.025em;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.4rem;
  line-height: 1.4;
`;

const Company = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--accent);
  margin-bottom: 0.75rem;
  
  a {
    color: var(--accent);
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--text-primary);
      text-decoration: underline;
    }
  }
`;

const Description = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
  margin-top: 0.5rem;

  a {
    color: var(--accent);
    text-decoration: none;
    transition: all 0.3s ease;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ScreenshotContainer = styled.div`
  margin-top: 0.5rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const Screenshot = styled.img`
  width: ${props => props.noIphone ? '80px' : '60px'};
  height: ${props => props.noIphone ? '80px' : '130px'};
  border-radius: 8px;
  object-fit: ${props => props.noIphone ? 'cover' : 'contain'};
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: var(--shadow-lg);
  }
  
  @media (max-width: 768px) {
    width: ${props => props.noIphone ? '60px' : '45px'};
    height: ${props => props.noIphone ? '60px' : '98px'};
  }
`;

const LineConnector = styled.div`
  position: absolute;
  top: 50%;
  right: -1px;
  width: 30px;
  height: 2px;
  background: ${props => props.color};
  z-index: 1;
  opacity: 0.3;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TimelineItem = ({ item, index }) => {
  const categoryStyle = categories[item.category];
  
  const itemVariants = {
    hidden: { 
      opacity: 0, 
      x: index % 2 === 0 ? -50 : 50,
      y: 20
    },
    visible: { 
      opacity: 1, 
      x: 0,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    hover: {
      rotate: [0, -10, 10, -10, 0],
      transition: { duration: 0.5 }
    }
  };

  return (
    <TimelineItemContainer
      className="timeline-item"
      variants={itemVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <IconContainer
        bgColor={categoryStyle.bgColor}
        variants={iconVariants}
        whileHover="hover"
      >
        <TimelineYear
          color={categoryStyle.color}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {item.year}
        </TimelineYear>
        {item.icon}
      </IconContainer>
      
      <LineConnector 
        className="line-connector" 
        color={categoryStyle.color} 
      />
      
      <Content className="content">
        <Card
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <Title>{item.title}</Title>
          <Company>
            {item.link ? (
              <a href={item.link} target="_blank" rel="noopener noreferrer">
                {item.company}
              </a>
            ) : (
              item.company
            )}
          </Company>
          {item.screenshots && item.screenshots.length > 0 && (
            <ScreenshotContainer>
              {item.screenshots.map((screenshot, index) => (
                <Screenshot
                  key={index}
                  src={screenshot}
                  alt={`${item.company} screenshot ${index + 1}`}
                  noIphone={item.noIphone}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ))}
            </ScreenshotContainer>
          )}
          <Description dangerouslySetInnerHTML={{ __html: item.description }} />
        </Card>
      </Content>
    </TimelineItemContainer>
  );
};

export default TimelineItem;